import { DateTime } from '../../graphql/DateTime';
import { PlanSummaryProduct } from '../../models/obapi_api/PlanSummary';
import { APIConstants, LOB, PopulationType } from '../Constants';
import { hcpTokenService } from '../hcp';

export const getContactValue = (
  contactPurposeTypeCode: string,
  products: PlanSummaryProduct[] = []
) =>
  products
    ?.find((product) => product?.productId === APIConstants.OBAPI.RALLYCONNECT)
    ?.contactInformation?.find(
      (contactInfo) =>
        contactInfo?.contactPurposeTypeCode?.code === contactPurposeTypeCode
    )?.contactValue;

export const isEffectiveToday = (
  startDate,
  endDate,
  isPreEffective = false
) => {
  if (!startDate) {
    return false;
  }
  const startDateObj = new Date(startDate.replace(/-/g, '/'));
  const endDateObj = endDate ? new Date(endDate.replace(/-/g, '/')) : null;
  const currentDate = DateTime.getCurrentDateTimeWithCentralOffset();

  currentDate.setHours(0, 0, 0, 0);
  startDateObj.setHours(0, 0, 0, 0);
  endDateObj?.setHours(0, 0, 0, 0);

  const isStartDateValid = startDateObj.getTime() <= currentDate.getTime();
  const isEndDateValid =
    endDateObj === null || currentDate.getTime() <= endDateObj.getTime();
  if (isPreEffective) {
    return isEndDateValid;
  }
  return isStartDateValid && isEndDateValid;
};

export const getBehavioralData = (products: PlanSummaryProduct[] = []) =>
  products?.find((product) => product?.productId === APIConstants.OBAPI.LAWW);

const planDetailsDateRangeFilter = (planDetails, isPreEffective) =>
  planDetails?.map((planDetail) => {
    const filteredBenefits = planDetail?.eligibility?.benefitSet?.filter(
      (benefits) => {
        const { startDate, stopDate } = benefits?.benefitPeriod ?? {};
        return isEffectiveToday(startDate, stopDate, isPreEffective);
      }
    );
    if (filteredBenefits?.length > 0) {
      planDetail.eligibility.benefitSet = filteredBenefits;
    }
    return planDetail;
  });

export const augmentProfileWithPlanDetails = (
  profile,
  planDetails,
  isUMR = false
) => {
  const filteredPlanDetails = planDetailsDateRangeFilter(
    planDetails,
    profile?.isPreEffective
  );
  const matchedDeptSqNmbr = profile?.map((memberInfo) => {
    const depSeqNbr = memberInfo?.demographics?.dependentSeqNbr;
    return filteredPlanDetails?.filter(
      (details) => isUMR || details?.dependentSeqNbr === depSeqNbr
    );
  });

  profile?.forEach((memberInfo: { planDetail: any }, index: number) => {
    if (matchedDeptSqNmbr[index].length) {
      [memberInfo.planDetail] = matchedDeptSqNmbr[index];
    }
  });
  // Populates memberInfo.isUrgentCareEligible by checking if member is eligible for urgent care under the MEDICAL coverageType
  // Iterating over all the members profiles and their planDetail.eligibility filtered object
  profile?.forEach((member: {
    isUrgentCareEligible: Boolean,
    planDetail: {
      planName?: String
      eligibility?: {
        ancillaryGroup?: { groupName?: String }
      }
    }}) => {
    const hasMedicalUrgentCare = filteredPlanDetails?.map((planDetail) => {
      const medicalBenefitSet = planDetail?.eligibility?.benefitSet?.find(
        (benefitSet) => benefitSet.coverageTypeCode.code === 'M'
      );
      return medicalBenefitSet?.serviceLevelBenefitSet?.serviceBenefit?.find(
        (serviceBenefit) =>
          serviceBenefit.service?.hipaaServiceTypeCode?.code === 'UC'
      );
    });
    member.isUrgentCareEligible =
      hasMedicalUrgentCare?.length > 0 && hasMedicalUrgentCare[0] !== undefined;
    if (member.planDetail) {
      member.planDetail.planName = member.planDetail?.eligibility?.ancillaryGroup?.groupName;
    }
  });

  return profile;
};

export const augmentProfileWithPcpChecks = (profile) => {
  profile?.forEach((memberInfo) => {
    const pcpEligibilityIndicator = memberInfo?.eligibility?.map(
      (eligibility) => {
        const medicalCoverageType =
          eligibility?.memberHealthCoverage?.coverageType?.find(
            (coverageType) => coverageType.typeCode.coverageTypeCode === 'M'
          );
        return medicalCoverageType?.pcpEligibilityIndicator;
      }
    );

    const isSubscriber =
      memberInfo.demographics.relationshipTypeCode.code === 'SUBSCRIBER';
    if (pcpEligibilityIndicator.length) {
      memberInfo.viewPcp = true;
      memberInfo.editPcp = true;
      memberInfo.viewDependentPcp = isSubscriber;
      memberInfo.editDependentPcp = isSubscriber;
    } else {
      memberInfo.viewPcp = false;
      memberInfo.editPcp = false;
      memberInfo.viewDependentPcp = false;
      memberInfo.editDependentPcp = false;
    }
  });

  return profile;
};

export const filterProfilesIfNotSubscriber = (profile) => {
  const requestingMember = profile?.memberInfo?.find(
    (memberInfo) => memberInfo?.requestingMemberIndicator === 'Y'
  );
  if (
    requestingMember?.demographics?.relationshipTypeCode?.code !== 'SUBSCRIBER'
  ) {
    profile.memberInfo = profile?.memberInfo?.filter(
      (memberInfo) => memberInfo?.requestingMemberIndicator === 'Y'
    );
  }
  return profile;
};

export const getIsUSPProviderGroup = (
  isUSPMember: boolean,
  primaryCareFirstName: string,
  providerGroupName: string
): boolean => isUSPMember && !primaryCareFirstName && !!providerGroupName;

export const getProviderTypeFromObapiType = (
  providerTypeCode,
  isUSPProviderGroup,
  lob?: string
): string => {
  if (
    isUSPProviderGroup ||
    providerTypeCode?.code === 'OTHER' ||
    (providerTypeCode?.code === 'GROUP' && lob === LOB.IFP)
  ) {
    return 'MEDICAL_GROUP';
  }
  return 'PRACTITIONER';
};

export const formatDob = (dob) => {
  const [year, month, date] = dob.split('-');
  return [month, date, year].join('/');
};
export const isMNRCosmosFeature = (parameters, enableMnrCosmos) => {
  const { portalSource, userName, id } = parameters;
  return !!(enableMnrCosmos && portalSource && userName && id);
};

export const isMnrMember = (enableMnrCosmos, lob) =>
  enableMnrCosmos && lob === LOB.MNR;

export const isCNSMember = (enableCns, lob) => enableCns && lob === LOB.CNS;

export const getIsPreEffective = (
  showIsPreEffectiveIndicator: boolean,
  lob: string,
  population: string,
  hasFutureCoverageEffectiveDate: boolean
): boolean =>
  showIsPreEffectiveIndicator &&
  hasFutureCoverageEffectiveDate &&
  (lob === LOB.EI ||
    lob === LOB.CNS ||
    lob === LOB.IFP ||
    (lob === LOB.MNR && population === PopulationType.COSMOS));

let kelseySeyboldData;
export async function getKelseySeyboldPlanData() {
  try {
    if (kelseySeyboldData === undefined) {
      kelseySeyboldData = await import(
        `../../../resources/kelseySeyboldPlans.json`
      );
    }

    return kelseySeyboldData?.default || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error reading kelseySeyboldPlans.json', error);
    return [];
  }
}

export const getIsKelseySeyboldPlan = (
  planData,
  policyNumber: string,
  reportingCode: string
): boolean =>
  !!planData?.find(
    (entry) =>
      entry?.policyNumber === policyNumber &&
      entry?.reportingCode === reportingCode
  );

export const getIsReferralRequired = (
  useNewReferralsIndicator: boolean,
  pcpReferralsIndicator: string,
  psxPcpReferralsIndicator: string
): boolean => {
  if (useNewReferralsIndicator) {
    return psxPcpReferralsIndicator === 'Y';
  }
  return pcpReferralsIndicator === 'Y';
};

export const getXUpstreamEnv = (isPreProdEnabled: boolean = false) => {
  if (process.env.APP_ENV === 'prod') return 'prod';
  if (
    (process.env.APP_ENV === 'dev' || process.env.APP_ENV === 'local' || process.env.APP_ENV === 'rc') &&
    !isPreProdEnabled
  )
    return 'stage';
  return 'beta';
};

export const getHcpTokenWithScope = (
  env: string,
  contextValue: any,
  scope?: string
) => hcpTokenService.getHCPToken(env, contextValue, scope);
